import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import { EmptyStateWrapper } from '../../style';
import { jobsActions } from '../../../../state/ducks/jobs';
import { filtersActions } from '../../../../state/ducks/filters';
import * as filterOperations from '../../../../state/ducks/filters/operations';
import SearchInput from '../SearchInput';

function FilterAccordionDetails(props) {
  const {
    sectionKey,
    parseData,
    endpoint,
    determineSearchParams,
    params,
    apiSearchKey,
    inputType,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [throttleTimeout, setThrottleTimeout] = useState(null);
  const query = useSelector((state) => state.filters.filterQueries[sectionKey]);
  const dispatch = useDispatch();
  const sectionFilterItems = useSelector(
    (state) => state.filters.filterItems[sectionKey]
  );
  const {
    [sectionKey]: sectionSelectedFilterItems,
    ...otherSelectedFilterItems
  } = useSelector((state) => state.filters.selectedFilterItems);

  function fetchFilters(searchQuery) {
    setIsLoading(true);
    dispatch(
      filterOperations.fetchFilterItems({
        sectionKey,
        searchQuery,
        apiSearchKey,
        determineSearchParams,
        endpoint,
        params,
        parseData,
      })
    )
      .then(() => {
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        setIsErrored(true);
      });
  }

  const fetchFiltersDebounce = (searchQuery) => {
    if (searchQuery) {
      setIsDebouncing(true);
      clearTimeout(throttleTimeout);
      setThrottleTimeout(
        setTimeout(() => {
          setIsDebouncing(false);
          fetchFilters(searchQuery);
        }, 300)
      );
    } else {
      fetchFilters(searchQuery);
    }
  };

  useDeepCompareEffect(() => {
    fetchFilters(query);
  }, [otherSelectedFilterItems, query === '']);

  const handleRenderItems = () => {
    if (isErrored) {
      return (
        <EmptyStateWrapper>
          Filter options failed to load.
          <Button
            variant="outlined"
            onClick={() => {
              fetchFilters(query);
            }}
            css={{
              marginTop: '12px',
            }}
          >
            Retry
          </Button>
        </EmptyStateWrapper>
      );
    }

    const allFilterItems = new Map([
      ...sectionFilterItems,
      ...sectionSelectedFilterItems,
    ]);
    if (!isDebouncing && !isLoading && isEmpty(allFilterItems)) {
      return <EmptyStateWrapper>No results found</EmptyStateWrapper>;
    }

    const determineUpdatedFilters = (checked, id, name) => {
      if (inputType === 'radio') {
        return new Map([[id, name]]);
      }
      if (checked) {
        const addFilter = new Map([...sectionSelectedFilterItems, [id, name]]);
        return addFilter;
      }
      const removeFilter = new Map(
        Array.from(sectionSelectedFilterItems).reduce(
          (accumulator, sectionSelectedFilterItem) =>
            sectionSelectedFilterItem[0] === id
              ? accumulator
              : [...accumulator, sectionSelectedFilterItem],
          []
        )
      );
      return removeFilter;
    };
    const selectedFilterNames = Array.from(
      sectionSelectedFilterItems.values()
    ).sort();
    return Array.from(allFilterItems)
      .sort(([, first], [, second]) =>
        selectedFilterNames.includes(first) &&
        !selectedFilterNames.includes(second)
          ? -1
          : 0
      )
      .map(([id, name]) => {
        const isChecked = Array.from(
          sectionSelectedFilterItems.keys()
        ).includes(id);
        return (
          <FormControlLabel
            id={`filter-item-${sectionKey}-${id}`}
            label={
              <Typography variant="body2" css={{ paddingTop: '11px' }}>
                {name}
              </Typography>
            }
            checked={isChecked}
            control={
              <Checkbox
                onChange={({ target: { checked } }) => {
                  dispatch(jobsActions.changeCurrentPage(0));
                  dispatch(
                    filtersActions.updateSelectedFilterItems({
                      [sectionKey]: determineUpdatedFilters(checked, id, name),
                    })
                  );
                }}
                size="small"
              />
            }
            size="small"
            css={{
              alignItems: 'flex-start',
            }}
          />
        );
      });
  };

  return (
    <AccordionDetails>
      {apiSearchKey && (
        <SearchInput
          isLoading={isLoading}
          isDisabled={isErrored}
          value={query}
          onChange={({ target: { value } }) => {
            fetchFiltersDebounce(value);
            dispatch(
              filtersActions.updateFilterQueries({ [sectionKey]: value })
            );
          }}
          sectionKey={sectionKey}
        />
      )}
      <Box css={{ maxHeight: '400px', overflowY: 'scroll' }}>
        <FormGroup>{handleRenderItems()}</FormGroup>
      </Box>
    </AccordionDetails>
  );
}

FilterAccordionDetails.defaultProps = {
  displayFormat: (props) => props.name,
  inputType: 'checkbox',
};

export default FilterAccordionDetails;
